/**
 * Tracked-brand ("competitor") name matching — the single matcher shared by
 * the metric writer (`computeCompetitorMetricsRecord`) and the answer
 * drill-down (`brandsNamed`).
 *
 * The two surfaces MUST agree: a number on the Brands page and the list of
 * brands printed under a raw answer are the same measurement seen at two
 * zoom levels, and a reader who counts the L3 rows has to land on the L2
 * total. Both therefore resolve a tracked brand's canonical display name and
 * its match aliases here, and both count only mentions with
 * `inSummary === true`.
 *
 * Pure — no db, no server-only imports. Safe for `@repo/core/client`.
 */

import { isCompetitorBrand } from "./discovery";

/** A tracked brand as the property stores it. */
export interface CompetitorLike {
	url?: string | null;
	name?: string | null;
	aliases?: string[] | null;
}

/** One tracked brand reduced to "what to display" + "what to match on". */
export interface CompetitorMatcher {
	/** Canonical display name — the key every stored slice is written under. */
	competitorName: string;
	/** Lower-cased name + aliases handed to `isCompetitorBrand`. */
	matchNames: string[];
}

/** Domain part of a competitor URL, for display fallback when it has no name. */
export function normalizeCompetitorDomain(domain: string): string {
	return domain
		.toLowerCase()
		.replace(/^https?:\/\//, "")
		.replace(/^www\./, "")
		.replace(/\/$/, "")
		.split("/")[0];
}

/**
 * Resolve each tracked brand to its canonical name + match aliases, in the
 * property's own competitor order. A brand with neither name nor parseable
 * url is named "Unknown" — the same fallback the metric writer has always
 * used, kept so historical rows and fresh ones key identically.
 */
export function buildCompetitorMatchers(
	competitors: readonly CompetitorLike[] | null | undefined,
): CompetitorMatcher[] {
	return (competitors ?? []).map((competitor) => {
		const domain = normalizeCompetitorDomain(competitor.url ?? "");
		const displayName = competitor.name || domain;
		const matchNames: string[] = [];
		if (displayName) {
			matchNames.push(displayName.toLowerCase());
		}
		for (const alias of competitor.aliases ?? []) {
			if (alias && typeof alias === "string") {
				matchNames.push(alias.toLowerCase());
			}
		}
		return { competitorName: displayName || "Unknown", matchNames };
	});
}

/** A brand mention as both the writer and the drill-down see it. */
export interface BrandMentionLike {
	brandName: string;
	inSummary?: boolean | null;
}

/**
 * How many times one tracked brand is named in an answer's summary. Zero
 * means "the answer did not name it"; the writer turns a non-zero count into
 * one appearance plus that many mentions.
 */
export function countCompetitorMentionsInSummary(
	brandMentions: readonly BrandMentionLike[] | null | undefined,
	matcher: CompetitorMatcher,
): number {
	let count = 0;
	for (const mention of brandMentions ?? []) {
		if (mention?.inSummary !== true) continue;
		if (isCompetitorBrand(mention.brandName, matcher.matchNames)) {
			count++;
		}
	}
	return count;
}

/**
 * The tracked brands named in one answer's summary, as canonical names in
 * matcher order. This is exactly the set that contributes an "appearance" to
 * that answer in `computeCompetitorMetricsRecord`, so an L3 list of these
 * names reconciles against the L2 counts row by row.
 */
export function competitorsNamedInSummary(
	brandMentions: readonly BrandMentionLike[] | null | undefined,
	matchers: readonly CompetitorMatcher[],
): string[] {
	const named: string[] = [];
	for (const matcher of matchers) {
		if (countCompetitorMentionsInSummary(brandMentions, matcher) > 0) {
			named.push(matcher.competitorName);
		}
	}
	return named;
}
